import { observable } from "@legendapp/state";
import { syncedCrud } from "@legendapp/state/sync-plugins/crud";
import type { CreateMemoryThreadResponse } from "@mastra/client-js";
import { nanoid } from "@repo/shared/utils/id";
import type * as Ably from "ably";

import { mastraClient } from "~/libs/mastra.lib";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "~/stores/app-data-ready";
import { auth$ } from "~/stores/auth";
import { activeOrganization$ } from "~/stores/organizations";

const AGENT_ID = "supervisor-agent";

/** Builds the Mastra memory resource ID from org + user, matching the server pattern. */
export const buildResourceId = (): string | null => {
  const orgId = activeOrganization$.id.get();
  const userId = auth$.user.id.get();
  if (!orgId || !userId) return null;
  return `${orgId}:${userId}`;
};

/**
 * App-specific fields stored in the Mastra thread `metadata` JSONB. Threads
 * have no dedicated columns for these, so they live alongside any Mastra
 * internal metadata keys.
 */
export type ThreadMetadata = {
  /** Whether the user favorited this thread (surfaces it in the sidebar Favorites). */
  favorite?: boolean;
  /** Set when the user manually renamed the thread, so the async LLM title upgrade skips it. */
  titleSetByUser?: boolean;
};

/**
 * Thread object as returned by the Mastra memory API, narrowed so `metadata`
 * is always present and typed with our app-specific {@link ThreadMetadata}.
 */
export type Thread = Omit<CreateMemoryThreadResponse, "metadata"> & {
  metadata?: ThreadMetadata & Record<string, unknown>;
};

/**
 * Recent threads for the current user/org, keyed by ID.
 * Threads are created lazily on the backend when the first message is sent.
 */
export const threads$ = observable(
  syncedCrud<Thread>({
    waitFor: appDataReady$,
    // This store uses the raw syncedCrud (not the app's configured
    // syncedCrudPersist), so opt into partial + debounced updates explicitly
    // to match board/dataset write behavior.
    updatePartial: true,
    debounceSet: 400,
    list: async () => {
      const resourceId = buildResourceId();
      if (!resourceId) return [];

      const { threads } = await mastraClient.listMemoryThreads({
        resourceId,
        agentId: AGENT_ID,
        // Keep the payload bounded but high enough that favorited/renamed
        // threads older than the most recent handful don't silently vanish.
        perPage: 50,
        orderBy: { field: "createdAt", direction: "DESC" },
      });

      return threads as Thread[];
    },
    update: async (input) => {
      if (!input.id) return;
      await trpc.ai.updateThread.mutate({
        threadId: input.id,
        title: input.title,
        favorite: input.metadata?.favorite,
      });
    },
    delete: async (input) => {
      if (!input.id) return;
      await trpc.ai.deleteThread.mutate({ threadId: input.id });
    },
    subscribe: ({ update }) => {
      const merge = (data: Ably.Message["data"]) => {
        update({
          mode: "merge",
          value: Array.isArray(data) ? data : [data],
        });
      };
      const handler = (message: Ably.Message) => merge(message.data);

      // Title broadcasts are fire-and-forget on the server and Ably realtime
      // has no replay, so a broadcast published in the gap before we subscribe
      // would otherwise be lost until reload. Pull recent "threads" history
      // once on attach to recover any just-missed title. Read-only and
      // event-name scoped, so it doesn't affect other shared-channel listeners.
      const recoverMissed = (
        channel: NonNullable<ReturnType<typeof getOrgChannel>>,
      ) => {
        channel
          .history({ limit: 20, direction: "backwards" })
          .then((page) => {
            for (const message of page.items) {
              if (message.name === "threads") merge(message.data);
            }
          })
          .catch(() => {
            // History is best-effort; the polling fallback recovers otherwise.
          });
      };

      // The org channel is created by `switchOrgChannel` on org change, which
      // can race with this store activating. If it isn't ready yet, retry
      // until it is so title broadcasts aren't permanently dropped on startup.
      let channel: ReturnType<typeof getOrgChannel> = null;
      let retry: ReturnType<typeof setTimeout> | undefined;

      const attach = () => {
        channel = getOrgChannel();
        if (!channel) {
          retry = setTimeout(attach, 500);
          return;
        }
        channel.subscribe("threads", handler);
        recoverMissed(channel);
      };

      attach();

      return () => {
        if (retry) clearTimeout(retry);
        channel?.unsubscribe("threads", handler);
      };
    },
  }),
);

/**
 * Currently selected thread ID. Always has a value — either from an
 * existing thread loaded from the server or a locally generated nanoid
 * for a new conversation (the backend creates the thread on first message).
 */
export const activeThreadId$ = observable<string | null>(null);

threads$.onChange(
  ({ value }: { value: Record<string, Thread> | undefined }) => {
    if (!activeThreadId$.get()) {
      const firstThread = value ? Object.values(value)[0] : undefined;
      activeThreadId$.set(firstThread?.id ?? nanoid());
    }
  },
);

// Thread IDs are scoped to an org's resourceId on the server. When the active
// org changes we must drop the current thread so it isn't sent back to the
// server under the wrong resourceId (which throws a thread/resource mismatch).
activeOrganization$.id.onChange(() => {
  activeThreadId$.set(null);
});

/** Resolved active thread object from the threads map. */
export const activeThread$ = observable(() => {
  const id = activeThreadId$.get();
  if (!id) return null;
  return threads$[id].get() ?? null;
});
